import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CallCentersState } from "./call-centers.types";
import { TCallCenter } from "@/models/call-centers.model";
import { logout } from "@/store/reducers/auth.reducer";
import {
  fetchCallCenters,
  fetchCallCenter,
  createCallCenter,
  updateCallCenter,
  deleteCallCenter,
} from "./call-centers.thunks";

const initialState: CallCentersState = {
  callCenters: [],
  selectedCallCenter: null,
  selectedCallCenterLoading: false,
  loading: false,
  selectionReady: false,
};

const callCentersSlice = createSlice({
  name: "callCenters",
  initialState,
  reducers: {
    setSelectedCallCenter: (state, action: PayloadAction<TCallCenter | null>) => {
      state.selectedCallCenter = action.payload;
    },
    setSelectionReady: (state, action: PayloadAction<boolean>) => {
      state.selectionReady = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCallCenters.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCallCenters.fulfilled, (state, action) => {
        state.loading = false;
        const sorted = [...action.payload].sort((a, b) => b.id - a.id);
        state.callCenters = sorted;
        // Auto-select the first active call center when none is selected yet
        if (!state.selectedCallCenter) {
          const firstActive = sorted.find((cc) => cc.isCallCenterActive);
          if (firstActive) state.selectedCallCenter = firstActive;
        }
      })
      .addCase(fetchCallCenters.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.loading = false;
      })
      .addCase(fetchCallCenter.pending, (state) => {
        state.selectedCallCenterLoading = true;
      })
      .addCase(fetchCallCenter.fulfilled, (state, action) => {
        state.selectedCallCenterLoading = false;
        state.selectedCallCenter = action.payload;
        const index = state.callCenters.findIndex((cc) => cc.id === action.payload.id);
        if (index !== -1) {
          state.callCenters[index] = action.payload;
        }
      })
      .addCase(fetchCallCenter.rejected, (state, action) => {
        // Aborted requests come from the user picking a different call center
        // mid-fetch — leave the loading flag set so the in-flight successor keeps it true.
        if (action.meta.aborted) return;
        state.selectedCallCenterLoading = false;
      })
      .addCase(createCallCenter.fulfilled, (state, action) => {
        state.callCenters.unshift(action.payload);
      })
      .addCase(updateCallCenter.fulfilled, (state, action) => {
        const index = state.callCenters.findIndex((cc) => cc.id === action.payload.id);
        if (index !== -1) {
          state.callCenters[index] = action.payload;
        }
        if (state.selectedCallCenter?.id === action.payload.id) {
          state.selectedCallCenter = action.payload;
        }
      })
      .addCase(deleteCallCenter.fulfilled, (state, action) => {
        state.callCenters = state.callCenters.filter((cc) => cc.id !== action.payload);
        if (state.selectedCallCenter?.id === action.payload) {
          state.selectedCallCenter = null;
        }
      })
      .addCase(logout, () => initialState);
  },
});

export const { setSelectedCallCenter, setSelectionReady } = callCentersSlice.actions;
export default callCentersSlice.reducer;
